/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { DateAnnouncements } from '@app/core/models';
import { BaseService, DrupalConstants, HttpOptions } from '@makiwin/ngx-drupal8-rest';
import { map, mergeMap, Observable, tap } from 'rxjs';

/**
 * WEB-5297: Announcements service
 */
@Injectable({
  providedIn: 'root',
})
export class AnnouncementsService extends BaseService {
  unreadCount?: number;

  /**
   * Refresh current user unread announcements count only without loading the actual entities
   * @returns Observable of object { count: number }
   */
  refreshUnreadCount(): Observable<{ count: number }> {
    const httpOptions: HttpOptions = {
      method: 'get',
      params: {
        user_id: DrupalConstants.Connection.current_user.uid,
      },
    };
    return this.request(httpOptions, '/api/v1/announcements/count').pipe(
      tap((data) => (this.unreadCount = data.count))
    );
  }

  /**
   * Mark announcement entity as read
   * @param id Announcement entity id
   * @returns Observable boolean status
   */
  markAsRead(id: number): Observable<{ message: string }> {
    const httpOptions: HttpOptions = {
      method: 'post',
      frags: [id],
      params: {
        user_id: DrupalConstants.Connection.current_user.uid,
        _format: 'json',
      },
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': DrupalConstants.Connection.csrf_token,
      },
    };
    return this.request(httpOptions, '/api/v1/announcements/{id}/read').pipe(
      // Refresh unread count and return same result
      mergeMap((data) => this.refreshUnreadCount().pipe(map(() => data)))
    );
  }

  /**
   * Get all announcements entities for current user, read and unread both by default
   * @param page page index, default is 0
   * @param unreadOnly only fetch unread announcements
   * @returns Observable of array of date grouped announcements
   */
  getAll(page = 0, unreadOnly = false): Observable<DateAnnouncements[]> {
    const httpOptions: HttpOptions = {
      method: 'get',
      params: {
        user_id: DrupalConstants.Connection.current_user.uid,
        page,
      },
    };
    if (unreadOnly) {
      (httpOptions.params as any).is_read = true;
    }
    return this.request(httpOptions, '/api/v1/announcements');
  }
}
